// Native Google Sign-In wrapper (@capgo/capacitor-social-login).
//
// Android/iOS only. The plugin is imported dynamically so it never lands in the
// web bundle and stays SSR-safe. We use *online* mode: one login returns the
// idToken (to mint a Supabase session via signInWithIdToken) AND an accessToken
// already scoped for Drive appData. We additionally pull a serverAuthCode so the
// backend can obtain a long-lived refresh token for offline Drive access.

export const DRIVE_APPDATA_SCOPE = 'https://www.googleapis.com/auth/drive.appdata';

// Configured in Google Cloud Console; exposed as public env (client IDs are not
// secret — the client *secret* lives only in the Netlify function).
const WEB_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID;
const IOS_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_IOS_CLIENT_ID;

let initialized = false;

async function getPlugin() {
  const mod = await import('@capgo/capacitor-social-login');
  return mod.SocialLogin;
}

async function ensureInitialized(): Promise<void> {
  if (initialized) return;
  const SocialLogin = await getPlugin();
  await SocialLogin.initialize({
    google: {
      webClientId: WEB_CLIENT_ID,
      iOSClientId: IOS_CLIENT_ID,
      iOSServerClientId: WEB_CLIENT_ID,
      mode: 'online',
    },
  });
  initialized = true;
}

export interface NativeGoogleResult {
  idToken: string | null;
  accessToken: string | null;
  serverAuthCode: string | null;
  email: string | null;
}

/**
 * Opens the native Google account picker, requesting Drive appData access.
 * Returns the idToken (Supabase auth), a ready-to-use accessToken, and a
 * serverAuthCode for the backend refresh-token exchange.
 *
 * NOTE: the exact field carrying the serverAuthCode from getAuthorizationCode()
 * needs confirmation on a real device (the plugin types it generically); both
 * candidate fields are tried. The backend tolerates either a serverAuthCode or
 * a refresh token, so this is the one piece to verify during native testing.
 */
export async function nativeGoogleSignIn(): Promise<NativeGoogleResult> {
  await ensureInitialized();
  const SocialLogin = await getPlugin();

  const { result } = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['openid', 'email', 'profile', DRIVE_APPDATA_SCOPE],
      forceRefreshToken: true,
    },
  });

  if ('responseType' in result && result.responseType === 'online') {
    let serverAuthCode: string | null = null;
    try {
      const auth = await SocialLogin.getAuthorizationCode({ provider: 'google' });
      serverAuthCode = auth.jwt ?? auth.accessToken ?? null;
    } catch {
      // Offline-access code not available — backend will fall back to whatever
      // it can; the UI surfaces a reconnect prompt if no refresh token results.
    }
    return {
      idToken: result.idToken,
      accessToken: result.accessToken?.token ?? null,
      serverAuthCode,
      email: result.profile?.email ?? null,
    };
  }

  // Offline-only response (not expected in online mode, handled for safety).
  const offline = result as { serverAuthCode?: string };
  return { idToken: null, accessToken: null, serverAuthCode: offline.serverAuthCode ?? null, email: null };
}
